import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import TextField from "components/common/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import { Calendar, Plus, Pencil, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import useIsMobile from "components/hooks/useIsMobile";
import { minutesToDisplay } from "components/tasks/utils";

export interface TimeEntry {
  hoursReported?: number;
  minutesReported?: number;
  description?: string;
  date?: number;
  dateYmd?: string;
}

export const getTotalFromTimeEntries = (timeEntries: TimeEntry[]): number => {
  return timeEntries.reduce((acc, entry) => {
    return acc + (entry.hoursReported || 0) * 60 + (entry.minutesReported || 0);
  }, 0);
};

export const defaultTimeEntry = (): TimeEntry => {
  return {
    hoursReported: 0,
    minutesReported: 0,
    description: "",
    date: Date.now(),
    dateYmd: format(new Date(), "yyyy-MM-dd"),
  };
};

const minuteOptions = [
  { title: "0 mins", mins: 0 },
  { title: "5 mins", mins: 5 },
  { title: "10 mins", mins: 10 },
  { title: "15 mins", mins: 15 },
  { title: "20 mins", mins: 20 },
  { title: "25 mins", mins: 25 },
  { title: "30 mins", mins: 30 },
  { title: "45 mins", mins: 45 },
];

const hourOptions = [
  { title: "0 hours", hours: 0 },
  { title: "1 hour", hours: 1 },
  { title: "2 hours", hours: 2 },
  { title: "3 hours", hours: 3 },
  { title: "4 hours", hours: 4 },
  { title: "5 hours", hours: 5 },
  { title: "6 hours", hours: 6 },
  { title: "7 hours", hours: 7 },
  { title: "8 hours", hours: 8 },
];

const TotalTimeDisplay = ({
  time,
  showHours = true,
}: {
  time: number;
  showHours?: boolean;
}) => {
  const totalTimeStr = showHours ? minutesToDisplay(time) : `${time} mins`;
  return (
    <Box sx={{ display: "flex", gap: "12px" }}>
      <Typography variant="body">Total time</Typography>
      <Typography variant="bodyHeavy">{totalTimeStr}</Typography>
    </Box>
  );
};

export const TimeTrackerForm = ({
  title,
  updateFormData,
  formData,
  totalInMinutes,
  onSave,
  onCancel,
  onDelete,
  isSaving,
  showHours = true,
}: {
  title: string;
  updateFormData: (updates: TimeEntry) => void;
  formData: TimeEntry;
  totalInMinutes: number;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isSaving?: boolean;
  showHours?: boolean;
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: "12px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Typography variant="bodyHeavy">{title}</Typography>
        {isMobile && <X size={16} color="#C14743" onClick={onDelete} />}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          gap: isMobile ? "12px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "12px" : "8px",
          }}
        >
          {showHours && (
            <Autocomplete
              freeSolo
              disableClearable
              options={hourOptions.map((option) => option.title)}
              value={
                formData.hoursReported !== undefined
                  ? `${formData.hoursReported} ${
                      formData.hoursReported === 1 ? "hour" : "hours"
                    }`
                  : ""
              }
              onInputChange={(event, newInputValue) => {
                const numericValue = newInputValue.replace(/\D/g, "");
                const hours = parseInt(numericValue, 10);
                if (!isNaN(hours)) {
                  updateFormData({ hoursReported: hours });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="hours"
                  sx={{
                    width: isMobile ? "100%" : "150px",
                    height: "48px",
                    div: { minHeight: "48px" },
                    input: {
                      marginTop: "0",
                      paddingLeft: "16px",
                      paddingRight: "0px",
                    },
                  }}
                />
              )}
            />
          )}

          <Autocomplete
            freeSolo
            disableClearable
            options={minuteOptions.map((option) => option.title)}
            value={formData.minutesReported?.toString() + " mins" || ""}
            onInputChange={(event, newInputValue) => {
              const numericValue = newInputValue.replace(/\D/g, "");
              const minutes = parseInt(numericValue, 10);
              if (!isNaN(minutes)) {
                updateFormData({ minutesReported: minutes });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="minutes"
                sx={{
                  width: isMobile ? "100%" : "150px",
                  height: "48px",
                  div: { minHeight: "48px" },
                  input: {
                    marginTop: "0",
                    paddingLeft: "16px",
                    paddingRight: "0px",
                  },
                }}
              />
            )}
          />
        </Box>
        <Box>
          <DatePicker
            value={
              formData.dateYmd
                ? parseISO(formData.dateYmd)
                : new Date(formData.date || Date.now())
            }
            onChange={(date: Date | null) => {
              if (date === null) return;
              updateFormData({
                date: date.getTime(),
                dateYmd: format(date, "yyyy-MM-dd"),
              });
            }}
            format="MMM dd"
            open={dateOpen}
            onClose={() => setDateOpen(false)}
            sx={{ maxWidth: isMobile ? "none" : "150px" }}
            slotProps={{
              textField: {
                onClick: () => setDateOpen(true),
                InputProps: {
                  sx: { minHeight: "48px", input: { marginTop: "0px" } },
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        cursor: "pointer",
                        paddingTop: "0px !important",
                        marginLeft: "12px",
                      }}
                    >
                      <Calendar />
                    </InputAdornment>
                  ),
                  endAdornment: null,
                },
              },
            }}
          />
        </Box>
      </Box>
      <Box>
        <TextField
          fullWidth
          placeholder="What did you do?"
          value={formData.description}
          type="text"
          onChange={(e) => updateFormData({ description: e.target.value })}
          sx={{
            div: { minHeight: "48px" },
            input: {
              marginTop: "0",
              paddingLeft: "16px",
              paddingRight: "8px",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: "20px" }}>
          <Button disabled={isSaving} onClick={onSave}>
            Save
          </Button>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
        {!isMobile && (
          <TotalTimeDisplay time={totalInMinutes} showHours={showHours} />
        )}
      </Box>
    </Box>
  );
};

export const TimeEntryDisplay = ({
  entries,
  onEditEntry,
  onDeleteEntry,
  showHours = true,
}: {
  entries: TimeEntry[];
  onEditEntry: (index: number) => void;
  onDeleteEntry: (index: number) => void;
  showHours?: boolean;
}) => {
  const isMobile = useIsMobile();
  const formatTime = (totalMinutes: number) =>
    showHours ? minutesToDisplay(totalMinutes) : `${totalMinutes} mins`;
  return (
    <Box sx={{ width: "100%" }}>
      {entries.map((entry, i) => {
        const totalMinutes =
          (entry.hoursReported || 0) * 60 + (entry.minutesReported || 0);
        return (
          <Box key={i} sx={{}}>
            {isMobile ? (
              <Box
                sx={{ display: "flex", flexDirection: "column" }}
                onClick={() => onEditEntry(i)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Typography variant="bodyHeavy">
                      {formatTime(totalMinutes)}
                    </Typography>
                  </Box>
                  {entry.date && (
                    <Typography variant="bodySmall" color="#6B6E7B">
                      {format(new Date(entry.date), "MMM dd")}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body">{entry.description}</Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover .edit-icon": {
                    visibility: "visible",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <Typography variant="bodyHeavy" sx={{ textWrap: "nowrap" }}>
                    {formatTime(totalMinutes)}
                  </Typography>
                  <Typography variant="body">{entry.description}</Typography>
                  {entry.date && (
                    <Typography variant="bodySmall" color="#6B6E7B">
                      {format(new Date(entry.date), "MMM dd")}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{ visibility: "hidden", cursor: "pointer" }}
                  className="edit-icon"
                >
                  <Pencil
                    onClick={() => onEditEntry(i)}
                    size={20}
                    color="#8E9598"
                  />
                  <X
                    onClick={() => onDeleteEntry(i)}
                    size={20}
                    color="#8E9598"
                  />
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const SimpleTimeTracker = ({
  timeEntries,
  onSave,
  showHours = true,
}: {
  timeEntries: TimeEntry[];
  onSave: (entries: TimeEntry[]) => void;
  showHours?: boolean;
}) => {
  const [entries, setEntries] = useState<TimeEntry[]>(timeEntries);
  const [formData, setFormData] = useState<TimeEntry>(defaultTimeEntry());
  const [formVisibility, setFormVisibility] = useState<
    "add" | "edit" | "closed"
  >("closed");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const totalInMinutes = getTotalFromTimeEntries(entries);

  const handleSave = () => {
    let newEntries: TimeEntry[];
    if (editingIndex !== null) {
      newEntries = entries.map((entry, i) =>
        i === editingIndex ? formData : entry,
      );
    } else {
      newEntries = [...entries, formData];
    }
    setEntries(newEntries);
    onSave(newEntries);
    setFormData(defaultTimeEntry());
    setEditingIndex(null);
    setFormVisibility("closed");
  };

  const handleEditEntry = (index: number) => {
    setEditingIndex(index);
    setFormData(entries[index]);
    setFormVisibility("edit");
  };

  const handleDeleteEntry = (index: number) => {
    const newEntries = [...entries];
    newEntries.splice(index, 1);
    setEntries(newEntries);
    onSave(newEntries);
    if (editingIndex === index) {
      setFormData(defaultTimeEntry());
      setEditingIndex(null);
      setFormVisibility("closed");
    }
  };

  return (
    <Box
      sx={{
        padding: "16px 24px",
        borderRadius: "16px",
        border: "1px solid #D4D4D4",
        marginBottom: "16px",
      }}
    >
      <Typography variant="bodyHeavy" sx={{ marginBottom: "12px" }}>
        Time tracker
      </Typography>
      {entries.length === 0 && formVisibility === "closed" && (
        <Typography
          variant="body"
          color="#6B6E7B"
          sx={{ marginBottom: "12px" }}
        >
          Track time spent on this task.
        </Typography>
      )}

      {entries.length > 0 && formVisibility !== "edit" && (
        <TimeEntryDisplay
          entries={entries}
          onEditEntry={handleEditEntry}
          onDeleteEntry={handleDeleteEntry}
          showHours={showHours}
        />
      )}

      {(formVisibility === "add" || formVisibility === "edit") && (
        <TimeTrackerForm
          title={
            formVisibility === "add" ? "New time record" : "Edit time record"
          }
          updateFormData={(updates: TimeEntry) => {
            setFormData((prevState) => ({
              ...prevState,
              ...updates,
            }));
          }}
          formData={formData}
          totalInMinutes={totalInMinutes}
          onSave={handleSave}
          onCancel={() => {
            setFormData(defaultTimeEntry());
            setEditingIndex(null);
            setFormVisibility("closed");
          }}
          onDelete={() => {
            if (editingIndex === null) return;
            handleDeleteEntry(editingIndex);
          }}
          showHours={showHours}
        />
      )}

      {formVisibility === "closed" && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: entries.length > 0 ? "12px" : "0",
          }}
        >
          <Button
            variant="text"
            onClick={() => {
              setFormData(defaultTimeEntry());
              setFormVisibility("add");
            }}
            startIcon={<Plus size={20} />}
            sx={{ height: "20px" }}
          >
            Add time
          </Button>
          {entries.length > 0 && (
            <TotalTimeDisplay time={totalInMinutes} showHours={showHours} />
          )}
        </Box>
      )}
    </Box>
  );
};
